import React, { useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from '../context/AuthContext';
import api, { API_BASE } from '../api';
import gbahsLogo from '../img/gbahs.png';
import MobileHome from '../pages/MobileHome';
import MobileAttendance from '../pages/MobileAttendance';
import TeacherSchedule from '../pages/TeacherSchedule';
import Chat from '../pages/Chat';
import MobileLessonPlans from '../pages/MobileLessonPlans';
import MobileMySubjects from '../pages/MobileMySubjects';
import SubjectManagement from '../pages/SubjectManagement';
import MaterialManagement from '../pages/MaterialManagement';
import MyProfile from '../pages/MyProfile';
import MobileClassStatus from '../pages/MobileClassStatus';
import MobileHours from '../pages/MobileHours';
import ExamScores from '../pages/ExamScores';
import ExamResults from '../pages/ExamResults';
import AbsenceStatus from '../pages/AbsenceStatus';
import Board from '../pages/Board';
import YoutubeMaterial from '../pages/YoutubeMaterial';

const API_ORIGIN = API_BASE.replace(/\/api$/, '');

const MENUS = [
  { key: 'chat', label: '채팅', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'teacher-schedule', label: '일정관리', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'attendance', label: '출결관리', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'lesson-plans', label: '교수계획', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'my-class-status', label: '수업현황', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'my-hours', label: '시수현황', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'exam-results', label: '평가결과', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'absence-status', label: '결시현황', roles: ['admin', 'staff'] },
  { key: 'my-subjects', label: '담당과목', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'subject-management', label: '지정과목', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'material-management', label: '표준과목', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'youtube-material', label: '유튜브 자료', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  { key: 'my-profile', label: '개인정보', roles: ['admin', 'staff', 'teacher', 'foreign'] },
];

const TITLE_BY_KEY = MENUS.reduce((acc, m) => ({ ...acc, [m.key]: m.label }), {
  home: '학생시수관리',
  'exam-scores': '평가입력',
  board: '게시판',
});

export default function MobileShell() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  const visibleMenus = MENUS.filter((m) => m.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    // 로그인 화면으로 돌아가지 않고 앱 자체를 종료한다 (앱 전용 동작, 웹은 그대로 로그인 화면으로 이동)
    CapacitorApp.exitApp();
  };

  useEffect(() => {
    let regListener;
    let actionListener;

    (async () => {
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') return;
      await PushNotifications.register();

      // 발급받은 FCM 토큰을 서버에 등록 - 채팅 새 글이 올라오면 이 토큰으로 알림이 온다.
      regListener = await PushNotifications.addListener('registration', (token) => {
        api.post('/teacher/device-token', { token: token.value }).catch(() => {});
      });
      // 알림을 탭해서 앱을 열었을 때 채팅 화면으로 바로 이동
      actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', () => {
        setActive('chat');
      });
    })();

    return () => {
      regListener?.remove();
      actionListener?.remove();
    };
  }, []);

  const navigate = (key) => {
    setActive(key);
    setMenuOpen(false);
  };

  const renderScreen = () => {
    switch (active) {
      case 'home':
        return <MobileHome onNavigate={navigate} />;
      case 'attendance':
        return <MobileAttendance />;
      case 'teacher-schedule':
        return <TeacherSchedule mobile />;
      case 'chat':
        return <Chat />;
      case 'lesson-plans':
        return <MobileLessonPlans />;
      case 'my-subjects':
        return <MobileMySubjects />;
      case 'subject-management':
        return <SubjectManagement />;
      case 'material-management':
        return <MaterialManagement />;
      case 'youtube-material':
        return <YoutubeMaterial />;
      case 'my-profile':
        return <MyProfile />;
      case 'my-class-status':
        return <MobileClassStatus />;
      case 'my-hours':
        return <MobileHours />;
      case 'exam-scores':
        return <ExamScores mobile onNavigate={navigate} />;
      case 'exam-results':
        return <ExamResults mobile onNavigate={navigate} />;
      case 'absence-status':
        return <AbsenceStatus />;
      case 'board':
        return <Board />;
      default:
        return <MobileHome onNavigate={navigate} />;
    }
  };

  return (
    <div className="mobile-shell">
      <header className="mobile-topbar">
        <div className="mobile-topbar-title" onClick={() => navigate('home')}>
          <img src={gbahsLogo} alt="" className="mobile-topbar-logo" />
          {TITLE_BY_KEY[active] || '항공정비사과정관리'}
        </div>
        <div className="mobile-topbar-actions">
          <button className="mobile-menu-btn" type="button" onClick={() => setMenuOpen(true)}>☰</button>
          <button className="mobile-menu-btn" type="button" title="앱 종료" onClick={() => CapacitorApp.exitApp()}>✕</button>
        </div>
      </header>

      <main className="mobile-content">{renderScreen()}</main>

      {menuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-drawer-user">
              {user?.avatar_photo_url ? (
                <img src={`${API_ORIGIN}${user.avatar_photo_url}`} alt="" className="mobile-drawer-avatar-img" />
              ) : (
                <span className="mobile-drawer-avatar-emoji">{user?.avatar || ''}</span>
              )}
              {user?.name} 선생님
            </div>
            {visibleMenus.map((m) => (
              <button
                key={m.key}
                className={`mobile-drawer-item ${active === m.key ? 'active' : ''}`}
                type="button"
                onClick={() => navigate(m.key)}
              >
                {m.label}
              </button>
            ))}
            <div className="mobile-drawer-divider" />
            <button className="mobile-drawer-item" type="button" onClick={handleLogout}>로그아웃</button>
          </div>
        </div>
      )}
    </div>
  );
}
